import { apiClient } from '../api/apiClient';

export interface AppointmentWithDetails {
  id: number;
  appointment_date: string;
  status: string;
  appointment_type: string;
  patient_name?: string;
  patient_email?: string;
  doctor_name?: string;
  doctor_email?: string;
  /** Duration in minutes - from backend duration_minutes */
  duration?: number;
  duration_minutes?: number;
  notes?: string;
}

export interface ConsultationRequest {
  id: number;
  user_question?: string;
  context?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  ai_summary?: string;
  doctor_notes?: string;
}

export interface DashboardMetrics {
  totalAppointments: number;
  completed: number;
  pending: number;
  cancelled: number;
}

export interface DashboardData {
  metrics: DashboardMetrics;
  upcomingAppointments: AppointmentWithDetails[];
  aiRequests: ConsultationRequest[];
}

export interface AvailableSlot {
  id: string;
  date: string;
  time: string;
  appointment_date: string;
}

export interface RescheduleOptions {
  appointment_id: number;
  current_appointment_date: string;
  available_slots: AvailableSlot[];
  message?: string;
}

/** Response from GET /api/v1/appointments/available-slots/{doctor_id} */
export interface AvailableSlotsResponse {
  available_dates: Array<{ date: string; day_name: string; is_selected?: boolean; slot_duration?: number }>;
  available_slots: Array<{ time: string; time_24h: string; is_available?: boolean; is_booked?: boolean }>;
  booked_slots?: string[];
  message?: string;
}

/** Payload for doctor creating an appointment for a patient */
export interface CreateAppointmentForPatientPayload {
  title: string;
  description?: string;
  appointment_date: string; // ISO datetime (naive "YYYY-MM-DDTHH:MM:SS" when timezone is sent)
  duration_minutes?: number;
  status?: string;
  appointment_type?: string;
  patient_notes?: string;
  doctor_notes?: string;
  timezone?: string; // e.g. Asia/Kolkata so backend interprets appointment_date in this zone
}

class DashboardService {
  /**
   * Get doctor's appointments
   */
  async getAppointments(
    status?: string,
    limit: number = 100,
    skip: number = 0
  ): Promise<AppointmentWithDetails[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());

    const response = await apiClient.get<AppointmentWithDetails[]>(
      `/api/v1/appointments?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get doctor's consultation requests (AI Requests)
   */
  async getConsultationRequests(): Promise<ConsultationRequest[]> {
    const response = await apiClient.get<ConsultationRequest[]>(
      '/api/v1/doctors/my-consultation-requests'
    );
    return response.data;
  }

  /**
   * Update an appointment
   * Note: backend expects duration_minutes; we send duration as duration_minutes
   */
  async updateAppointment(
    appointmentId: number,
    update: { status?: string; appointment_date?: string; duration?: number; duration_minutes?: number; notes?: string; appointment_type?: string }
  ): Promise<AppointmentWithDetails> {
    const { duration, ...rest } = update;
    const payload = duration !== undefined ? { ...rest, duration_minutes: duration } : rest;
    const response = await apiClient.put<AppointmentWithDetails>(
      `/api/v1/appointments/${appointmentId}`,
      payload
    );
    return response.data;
  }

  /**
   * Get available slots for rescheduling an appointment
   */
  async getRescheduleOptions(appointmentId: number): Promise<RescheduleOptions> {
    const response = await apiClient.get<RescheduleOptions>(
      `/api/v1/doctors/appointments/${appointmentId}/reschedule-options`
    );
    return response.data;
  }

  /**
   * Reschedule an appointment (doctor initiates)
   */
  async rescheduleAppointment(
    appointmentId: number,
    newAppointmentDate: string,
    reason?: string
  ): Promise<AppointmentWithDetails> {
    const response = await apiClient.post<AppointmentWithDetails>(
      `/api/v1/doctors/appointments/${appointmentId}/reschedule`,
      {
        new_appointment_date: newAppointmentDate,
        reason: reason || null,
      }
    );
    return response.data;
  }

  /**
   * Get a single appointment by ID
   */
  async getAppointmentById(appointmentId: number): Promise<AppointmentWithDetails> {
    const response = await apiClient.get<AppointmentWithDetails>(
      `/api/v1/appointments/${appointmentId}`
    );
    return response.data;
  }

  /**
   * Get available appointment slots for a doctor on a date (for creating new appointments).
   * @param practiceId Optional practice ID; when doctor has multiple practices, use this to get slots for that practice.
   */
  async getAvailableSlots(doctorId: number, dateStr: string, practiceId?: number | null): Promise<AvailableSlotsResponse> {
    const params: Record<string, string | number> = { date_str: dateStr };
    if (practiceId != null) params.practice_id = practiceId;
    const response = await apiClient.get<AvailableSlotsResponse>(
      `/api/v1/appointments/available-slots/${doctorId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Create an appointment for a patient (doctor only). Uses POST /api/v1/doctors/patients/{patientId}/appointments.
   */
  async createAppointmentForPatient(
    patientId: number,
    payload: CreateAppointmentForPatientPayload
  ): Promise<AppointmentWithDetails> {
    const response = await apiClient.post<AppointmentWithDetails>(
      `/api/v1/doctors/patients/${patientId}/appointments`,
      payload
    );
    return response.data;
  }

  /**
   * Get dashboard data including metrics, appointments, and AI requests
   * @param startDate Optional start date for filtering (ISO date string YYYY-MM-DD)
   * @param endDate Optional end date for filtering (ISO date string YYYY-MM-DD)
   */
  async getDashboardData(startDate?: string, endDate?: string): Promise<DashboardData> {
    try {
      // Fetch all data in parallel
      const [allAppointments, aiRequests] = await Promise.all([
        this.getAppointments(undefined, 100, 0),
        this.getConsultationRequests(),
      ]);

      // Filter appointments by date range if provided
      let filteredAppointments = allAppointments;
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        
        filteredAppointments = allAppointments.filter((apt) => {
          if (!apt.appointment_date) return false;
          const aptDate = new Date(apt.appointment_date);
          return aptDate >= start && aptDate <= end;
        });
      }

      // Filter upcoming appointments (end time in future, not completed/cancelled/missed)
      const now = new Date();
      const upcomingAppointments = allAppointments
        .filter((apt) => {
          if (!apt.appointment_date) return false;
          const aptDate = new Date(apt.appointment_date);
          // Calculate appointment end time (start + duration in minutes)
          const durationMinutes = apt.duration_minutes ?? apt.duration ?? 30; // Default to 30 minutes if not provided
          const appointmentEndTime = new Date(aptDate.getTime() + durationMinutes * 60 * 1000);
          const isFuture = appointmentEndTime > now;
          const status = apt.status?.toLowerCase() || '';
          const isNotMissed = status !== 'missed';
          const isNotCancelled = status !== 'cancelled';
          const isNotCompleted = status !== 'completed';
          // Show in upcoming if: appointment end time is in future AND status is not completed/cancelled/missed
          return isFuture && isNotMissed && isNotCancelled && isNotCompleted;
        })
        .sort((a, b) => {
          const dateA = new Date(a.appointment_date || 0);
          const dateB = new Date(b.appointment_date || 0);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 5); // Get top 5 upcoming

      // Calculate metrics based on filtered appointments
      const metrics: DashboardMetrics = {
        totalAppointments: filteredAppointments.length,
        completed: filteredAppointments.filter((apt) => apt.status === 'completed').length,
        pending: filteredAppointments.filter(
          (apt) => apt.status === 'scheduled' || apt.status === 'confirmed'
        ).length,
        cancelled: filteredAppointments.filter(
          (apt) => apt.status === 'cancelled' || apt.status === 'missed'
        ).length,
      };

      // Filter pending AI requests (include requested, pending, and assigned statuses)
      const pendingAiRequests = aiRequests
        .filter((req) => {
          const status = req.status?.toLowerCase() || '';
          return status === 'requested' || status === 'pending' || status === 'assigned';
        })
        .sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB.getTime() - dateA.getTime(); // Most recent first
        })
        .slice(0, 5); // Get top 5 most recent

      return {
        metrics,
        upcomingAppointments,
        aiRequests: pendingAiRequests,
      };
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();

